import type { ProductCtaType, ProductStatus, ProductTranslationCompleteness } from '@today/types'
import { apiClient } from './api.client'
import type { Paged } from './customer.service'

/** Product catalog API client (TO-416). Shapes mirror the product-service DTOs (camelCase). */

export interface ProductDTO {
	productId: string
	code: string
	name: string
	defaultLocale: string
	status: ProductStatus
	categoryId: string | null
	partnerAttributes: Record<string, unknown>
	featured: boolean
	icon: string | null
	imageFileId: string | null
	ctaPrimaryType: ProductCtaType | null
	ctaPrimaryTarget: string | null
	ctaSecondaryType: ProductCtaType | null
	ctaSecondaryTarget: string | null
	createdAt: string
	updatedAt: string
}

export interface CategoryDTO {
	categoryId: string
	code: string
	name: string
	icon: string | null
	sortOrder: number
}

export interface TranslationDTO {
	locale: string
	name: string
	tagline: string | null
	shortDescription: string | null
	longDescription: string | null
	benefits: string[]
	mainCtaLabel: string | null
	secondaryCtaLabel: string | null
}

export interface TranslationsResponse {
	data: TranslationDTO[]
	completeness: ProductTranslationCompleteness
}

export interface CategoryBody {
	code: string
	name: string
	icon: string | null
	sortOrder: number
}

const EMPTY_PAGE = <T>(page: number, limit: number): Paged<T> => ({ data: [], total: 0, page, limit, totalPages: 0 })

/** GET a paginated list; an empty result is `200 {data:[],total:0}` (TO-632, AD-21.7). */
const getPage = async <T>(url: string, params: Record<string, string>): Promise<Paged<T>> => {
	const response = await apiClient.get<Paged<T> | ''>(url, { params })
	const page = Number(params.page ?? 1)
	const limit = Number(params.limit ?? 20)
	return response.data ? response.data : EMPTY_PAGE<T>(page, limit)
}

const product = (id: string) => `/products/${encodeURIComponent(id)}`
const category = (id: string) => `/products/categories/${encodeURIComponent(id)}`

export const productService = {
	list: (params: Record<string, string>) => getPage<ProductDTO>('/products', params),
	get: async (id: string) => (await apiClient.get<ProductDTO>(product(id))).data,
	create: async (body: Record<string, unknown>) => (await apiClient.post<ProductDTO>('/products', body)).data,
	update: async (id: string, body: Record<string, unknown>) =>
		(await apiClient.patch<ProductDTO>(product(id), body)).data,
	remove: async (id: string): Promise<void> => {
		await apiClient.delete(product(id))
	},
	transition: async (id: string, targetStatus: ProductStatus) =>
		(await apiClient.post<ProductDTO>(`${product(id)}/transitions`, { targetStatus })).data,
	translations: async (id: string) => (await apiClient.get<TranslationsResponse>(`${product(id)}/translations`)).data,
	saveTranslation: async (id: string, locale: string, body: Record<string, unknown>) =>
		(await apiClient.put<TranslationDTO>(`${product(id)}/translations/${encodeURIComponent(locale)}`, body)).data,
	deleteTranslation: async (id: string, locale: string): Promise<void> => {
		await apiClient.delete(`${product(id)}/translations/${encodeURIComponent(locale)}`)
	},
	/** Every category, all pages at limit=100, in the API's order (sortOrder, then name). */
	allCategories: async (): Promise<CategoryDTO[]> => {
		const all: CategoryDTO[] = []
		for (let page = 1; ; page++) {
			const result = await getPage<CategoryDTO>('/products/categories', { page: String(page), limit: '100' })
			all.push(...result.data)
			if (page >= result.totalPages) return all
		}
	},
	createCategory: async (body: CategoryBody) => (await apiClient.post<CategoryDTO>('/products/categories', body)).data,
	updateCategory: async (id: string, body: Partial<CategoryBody>) =>
		(await apiClient.patch<CategoryDTO>(category(id), body)).data,
	deleteCategory: async (id: string): Promise<void> => {
		await apiClient.delete(category(id))
	},
}
